import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import {
  debounce,
  throttle,
  requestIdleCallback,
  onDocumentLoaded,
  isDesktopBreakpoint,
  mediaQueryLarge,
} from '@theme/utilities';

function animationSpeedMs(element) {
  const value = getComputedStyle(element).getPropertyValue('--animation-speed');
  const seconds = parseFloat(value);
  return Number.isFinite(seconds) ? seconds * 1000 : 300;
}

// Panel bodies come from templates/search.apc-megamenu-panels.liquid, once per session per variant.
const panelHtml = new Map();

function loadPanels(root, variant) {
  const { megamenuEndpoint, megamenuMenu, cardsPerGrid } = root.dataset;
  const url = `${megamenuEndpoint}&q=${encodeURIComponent(`${megamenuMenu}:${cardsPerGrid}:${variant}`)}`;
  // Locale is required: country is NL for both / and /en. v2 busts sessions that
  // cached Eindhoven hours from before the EN translation was Closed.
  const key = `apc-mm:v2:${window.Shopify?.theme?.id}:${window.Shopify?.locale}:${window.Shopify?.country}:${url}`;
  if (panelHtml.has(key)) return panelHtml.get(key);

  const useStorage = !window.Shopify?.designMode;
  let stored = null;
  try {
    if (useStorage) stored = sessionStorage.getItem(key);
  } catch {}

  const html = stored
    ? Promise.resolve(stored)
    : fetch(url)
        .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
        .then((text) => {
          if (!text.includes('data-megamenu-panel-body')) throw new Error('empty response');
          try {
            if (useStorage) sessionStorage.setItem(key, text);
          } catch {}
          return text;
        })
        .catch((error) => {
          panelHtml.delete(key);
          console.warn('apc-megamenu: panels failed to load', error);
          throw error;
        });

  panelHtml.set(key, html);
  return html;
}

/**
 * Injects the fetched bodies into the empty shells in `scope`, or a retry notice when the fetch fails.
 * Resolves true when the DOM changed.
 */
async function fillPanels(root, scope, variant) {
  const empty = [...scope.querySelectorAll('[data-megamenu-panel]')].filter(
    (panel) => panel.querySelector('[data-megamenu-body]')?.childElementCount === 0,
  );
  if (empty.length === 0) return false;

  let doc;
  try {
    doc = new DOMParser().parseFromString(await loadPanels(root, variant), 'text/html');
  } catch {
    for (const panel of empty) {
      panel.querySelector('[data-megamenu-body]').replaceChildren(root.refs.retry.content.cloneNode(true));
    }
    return true;
  }

  for (const panel of empty) {
    const body = doc.querySelector(`[data-megamenu-panel-body="${panel.dataset.megamenuPanel}"]`);
    if (body) panel.querySelector('[data-megamenu-body]').replaceChildren(...body.childNodes);
  }
  return true;
}

/** Empties every shell that shows the retry notice so `fillPanels` fetches again. */
function clearRetryNotices(scope) {
  for (const notice of scope.querySelectorAll('[data-megamenu-retry]')) notice.parentElement.replaceChildren();
}

function isRetryClick(event) {
  return event.target instanceof Element && event.target.closest('[data-megamenu-retry-button]') !== null;
}

class ApcMegamenu extends Component {
  #controller = null;
  #activeKey = null;
  #debouncedClose = null;
  #suppressFocusOpen = false;
  #previousScrollY = 0;
  #panelHeights = new Map();
  #pendingKey = null;

  connectedCallback() {
    super.connectedCallback();

    const { desktop, backdrop } = this.refs;
    if (!desktop) return;

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#debouncedClose = debounce(this.#closeMenu, 150);

    const triggers = desktop.querySelectorAll('[data-megamenu-trigger]');
    for (const trigger of triggers) {
      trigger.addEventListener('mouseenter', this.#onTriggerEnter, { signal });
      trigger.addEventListener('mouseleave', this.#onTriggerLeave, { signal });
      trigger.addEventListener('focus', this.#onTriggerFocus, { signal });
    }

    for (const panel of desktop.querySelectorAll('[data-megamenu-panel]')) {
      panel.addEventListener('mouseenter', this.#keepActive, { signal });
      panel.addEventListener('mouseleave', this.#onPanelLeave, { signal });
    }

    backdrop?.addEventListener('mouseenter', this.#closeMenu, { signal });
    desktop.addEventListener('click', this.#onRetryClick, { signal });

    this.addEventListener('focusout', this.#onFocusOut, { signal });
    document.addEventListener('keydown', this.#onKeyDown, { signal });
    window.addEventListener('resize', this.#onResize, { signal });
    window.addEventListener('scroll', this.#onLayoutChange, { passive: true, signal });

    // Load early (intent, idle, breakpoint change); the shells are only filled on first open.
    this.refs.bar?.addEventListener('mouseenter', this.#load, { once: true, signal });
    mediaQueryLarge.addEventListener('change', this.#load, { signal });
    if (window.Shopify?.designMode) this.#load();
    else onDocumentLoaded(() => requestIdleCallback(this.#load, { timeout: 3000 }));
  }

  #load = () => {
    loadPanels(this, isDesktopBreakpoint() ? 'desktop' : 'mobile').catch(() => {});
  };

  /**
   * Fills the shells if needed, then activates `key`. Resolves false when the pointer or focus moved on
   * while waiting, in which case nothing is opened.
   */
  #openPanel(key) {
    this.#pendingKey = key;
    return fillPanels(this, this.refs.desktop, 'desktop').then((changed) => {
      if (this.#pendingKey !== key) return false;
      if (changed) this.#clearPanelHeights();
      if (this.#activeKey === key) {
        this.#measurePanelHeights();
        this.#updatePanelsHeight(key);
      } else {
        this.#activate(key);
      }
      return true;
    });
  }

  #onRetryClick = (event) => {
    if (!isRetryClick(event) || this.#activeKey == null) return;
    clearRetryNotices(this.refs.desktop);
    this.#openPanel(this.#activeKey);
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#controller = null;
    this.#debouncedClose?.cancel();
    this.#unlockScroll();
  }

  get #isDesktop() {
    return window.matchMedia('(width >= 64rem)').matches;
  }

  #onTriggerEnter = (event) => {
    if (!this.#isDesktop) return;
    this.#openPanel(event.currentTarget.dataset.megamenuTrigger);
  };

  #onTriggerFocus = (event) => {
    if (!this.#isDesktop || this.#suppressFocusOpen) return;
    this.#openPanel(event.currentTarget.dataset.megamenuTrigger);
  };

  #onTriggerLeave = () => {
    if (!this.#isDesktop) return;
    this.#pendingKey = null;
    this.#debouncedClose();
  };

  #onPanelLeave = () => {
    this.#debouncedClose();
  };

  #keepActive = () => {
    this.#debouncedClose.cancel();
  };

  #activate = (key) => {
    this.#debouncedClose.cancel();
    if (this.#activeKey === key) return;

    const isSwitch = this.#activeKey != null;
    if (isSwitch) this.#setPanelState(this.#activeKey, false);
    this.#activeKey = key;
    if (!isSwitch) this.#lockScroll();
    this.#alignPanels();
    this.toggleAttribute('data-megamenu-switching', isSwitch);
    if (isSwitch) void this.refs.desktop.offsetHeight;
    this.#measurePanelHeights();
    this.#setPanelState(key, true);
    this.#updatePanelsHeight(key);
    this.setAttribute('data-megamenu-panel-open', 'true');
  };

  #measurePanelHeights = () => {
    const { panels } = this.refs;
    if (!panels || this.#panelHeights.size > 0) return;

    const measurements = [...panels.querySelectorAll('[data-megamenu-panel]')].map((panel) => {
      const inner = panel.querySelector('.apc-megamenu__panel-inner');
      panel.style.position = 'relative';
      panel.style.height = 'auto';
      if (inner instanceof HTMLElement) {
        inner.style.height = 'auto';
        inner.style.overflow = 'visible';
      }
      return { panel, inner };
    });

    void panels.offsetHeight;

    for (const { panel, inner } of measurements) {
      const target = inner instanceof HTMLElement ? inner : panel;
      this.#panelHeights.set(panel.dataset.megamenuPanel, target.scrollHeight);
      panel.style.position = '';
      panel.style.height = '';
      if (inner instanceof HTMLElement) {
        inner.style.height = '';
        inner.style.overflow = '';
      }
    }
  };

  #updatePanelsHeight = (key) => {
    const { panels } = this.refs;
    if (!panels) return;
    const height = this.#panelHeights.get(key);
    if (height) panels.style.height = `${height}px`;
  };

  #clearPanelHeights = () => {
    this.#panelHeights.clear();
  };

  #lockScroll = () => {
    this.#previousScrollY = window.scrollY;
    const gutter = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.#previousScrollY}px`;
    document.body.style.paddingRight = `${gutter}px`;
  };

  #unlockScroll = () => {
    if (document.body.style.position !== 'fixed') return;
    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.paddingRight = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
  };

  #alignPanels = () => {
    const { bar, panels } = this.refs;
    if (!bar || !panels) return;
    const offsetParent = panels.offsetParent;
    if (!(offsetParent instanceof HTMLElement)) return;
    const barBottom = bar.getBoundingClientRect().bottom;
    const parentTop = offsetParent.getBoundingClientRect().top;
    panels.style.top = `${barBottom - parentTop}px`;
  };

  #closeMenu = () => {
    this.#pendingKey = null;
    if (this.#activeKey == null) return;
    this.#setPanelState(this.#activeKey, false);
    this.#activeKey = null;
    this.removeAttribute('data-megamenu-panel-open');
    this.removeAttribute('data-megamenu-switching');
    this.#unlockScroll();
  };

  #setPanelState = (key, open) => {
    const panel = this.refs.desktop.querySelector(`[data-megamenu-panel="${key}"]`);
    const trigger = this.refs.desktop.querySelector(`[data-megamenu-trigger="${key}"]`);
    if (panel) {
      panel.hidden = false;
      if (open) {
        panel.setAttribute('data-megamenu-active', 'true');
        panel.setAttribute('aria-hidden', 'false');
      } else {
        panel.removeAttribute('data-megamenu-active');
        panel.setAttribute('aria-hidden', 'true');
      }
    }
    trigger?.setAttribute('aria-expanded', open ? 'true' : 'false');
  };

  #onLayoutChange = () => {
    if (this.#activeKey == null) return;
    this.#alignPanels();
  };

  #onResize = throttle(() => {
    if (this.#activeKey == null) return;
    this.#clearPanelHeights();
    this.#alignPanels();
    this.#measurePanelHeights();
    this.#updatePanelsHeight(this.#activeKey);
  }, 150);

  #onFocusOut = (event) => {
    if (!this.#isDesktop || this.#activeKey == null) return;
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
    this.#debouncedClose();
  };

  #onKeyDown = (event) => {
    if (!this.#isDesktop) return;

    const trigger = event.target instanceof Element ? event.target.closest('[data-megamenu-trigger]') : null;
    const inPanel = event.target instanceof Node && this.refs.panels?.contains(event.target);

    if (event.key === 'Escape') {
      if (this.#activeKey == null) return;
      this.#returnFocusToTrigger(this.#activeKey);
      this.#debouncedClose.cancel();
      this.#closeMenu();
      return;
    }

    if (trigger) {
      if (event.key === 'ArrowDown') {
        const key = trigger.dataset.megamenuTrigger;
        event.preventDefault();
        this.#openPanel(key).then((opened) => opened && this.#focusFirstInPanel(key));
      }
      return;
    }

    if (inPanel && this.#activeKey != null) this.#movePanelFocus(event);
  };

  #panelFocusables = (key) => {
    const panel = this.refs.desktop?.querySelector(`[data-megamenu-panel="${key}"]`);
    return panel ? [...panel.querySelectorAll('a[href], button:not([disabled])')] : [];
  };

  #focusFirstInPanel = (key) => {
    const first = this.#panelFocusables(key)[0];
    if (!first) return;
    const deadline = performance.now() + 300;
    const attempt = () => {
      if (this.#activeKey !== key) return;
      first.focus();
      if (document.activeElement !== first && performance.now() < deadline) {
        requestAnimationFrame(attempt);
      }
    };
    attempt();
  };

  #movePanelFocus = (event) => {
    const isArrow = event.key === 'ArrowDown' || event.key === 'ArrowUp';
    if (!isArrow && event.key !== 'Tab') return;
    const items = this.#panelFocusables(this.#activeKey);
    if (items.length === 0) return;

    const current = items.indexOf(document.activeElement);
    const forward = event.key === 'ArrowDown' || (event.key === 'Tab' && !event.shiftKey);

    if (event.key === 'ArrowUp' && current <= 0) {
      event.preventDefault();
      this.#returnFocusToTrigger(this.#activeKey);
      return;
    }

    event.preventDefault();
    const next = forward ? (current + 1) % items.length : (current - 1 + items.length) % items.length;
    items[next]?.focus();
  };

  #returnFocusToTrigger = (key) => {
    this.#suppressFocusOpen = true;
    this.refs.desktop?.querySelector(`[data-megamenu-trigger="${key}"]`)?.focus();
    this.#suppressFocusOpen = false;
  };
}

if (!customElements.get('apc-megamenu')) {
  customElements.define('apc-megamenu', ApcMegamenu);
}

class ApcMegamenuMobile extends Component {
  #controller = null;
  #activeKey = null;
  #previousScrollY = 0;
  #closeTimer = null;
  #trigger = null;

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('keydown', this.#onKeyDown, { signal });
    this.addEventListener('apc-menu-toggle:toggle', this.#onToggleRequest, { signal });
    window.addEventListener('resize', this.#onResize, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#controller = null;
    clearTimeout(this.#closeTimer);
  }

  get #isOpen() {
    return this.hasAttribute('data-megamenu-drawer-open');
  }

  #onToggleRequest = (event) => {
    this.#trigger = event.detail?.trigger ?? this.#trigger;
    if (this.#isOpen) {
      this.#close();
    } else {
      this.#open();
      loadPanels(this.closest('apc-megamenu'), 'mobile').catch(() => {});
    }
  };

  #open() {
    clearTimeout(this.#closeTimer);
    this.#updateOffset();
    this.#previousScrollY = window.scrollY;
    const gutter = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.#previousScrollY}px`;
    document.body.style.paddingRight = `${gutter}px`;

    this.hidden = false;
    void this.offsetHeight;
    this.setAttribute('data-megamenu-drawer-open', '');
    this.#trigger?.setAttribute('aria-expanded', 'true');

    trapFocus(this);
  }

  #close = () => {
    this.removeAttribute('data-megamenu-drawer-open');
    this.#trigger?.setAttribute('aria-expanded', 'false');

    removeTrapFocus();

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    this.#trigger?.focus();

    this.#closeTimer = setTimeout(() => {
      if (!this.#isOpen) {
        this.hidden = true;
        this.#closePanel();
      }
    }, animationSpeedMs(this));
  };

  #updateOffset = () => {
    let bottom = 0;
    for (const section of document.querySelectorAll('.shopify-section-group-header-group')) {
      bottom = Math.max(bottom, Math.max(0, section.getBoundingClientRect().bottom));
    }
    this.style.setProperty('--apc-megamenu-top', `${Math.round(bottom)}px`);
  };

  #onResize = () => {
    if (this.#isOpen) this.#updateOffset();
  };

  #onKeyDown = (event) => {
    if (event.key === 'Escape' && this.#isOpen) this.#close();
  };

  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const opener = target.closest('[data-megamenu-open]');
    if (opener && this.contains(opener)) {
      event.preventDefault();
      const key = opener.dataset.megamenuOpen;
      fillPanels(this.closest('apc-megamenu'), this, 'mobile').then(() => this.#isOpen && this.#openPanel(key));
      return;
    }

    if (isRetryClick(event)) {
      event.preventDefault();
      clearRetryNotices(this);
      fillPanels(this.closest('apc-megamenu'), this, 'mobile');
      return;
    }

    if (target.closest('[data-megamenu-back]')) {
      event.preventDefault();
      this.#closePanel();
    }
  };

  #openPanel(key) {
    this.#activeKey = key;
    this.#render();
  }

  #closePanel() {
    this.#activeKey = null;
    this.#render();
  }

  #render() {
    const activeKey = this.#activeKey ?? '0';
    const panels = this.querySelectorAll('[data-megamenu-panel]');

    for (const panel of panels) {
      const key = panel.getAttribute('data-megamenu-panel');
      let state;
      if (key === activeKey) {
        state = 'active';
      } else if (key === '0') {
        state = 'behind';
      } else {
        state = 'ahead';
      }
      panel.setAttribute('data-megamenu-state', state);
      panel.toggleAttribute('inert', state !== 'active');
      panel.scrollTop = 0;
    }

    for (const opener of this.querySelectorAll('[data-megamenu-open]')) {
      opener.setAttribute('aria-expanded', opener.dataset.megamenuOpen === this.#activeKey ? 'true' : 'false');
    }
  }
}

if (!customElements.get('apc-megamenu-mobile')) {
  customElements.define('apc-megamenu-mobile', ApcMegamenuMobile);
}
